import {
  isDecryptResult,
  isEncryptResult,
  isFieldsResult,
  isLockResult,
  isUnlockResult,
  isWorkerResponse
} from "./workerProtocol.js";
import type { FieldsResult, SealForExtensionRequest, WorkerRequest, WorkerResponse, WorkerResult } from "./workerProtocol.js";

export type ClientErrorCode = "worker-timeout" | "worker-error" | "bad-response" | "terminated";

export class ClientError extends Error {
  public readonly code: ClientErrorCode;

  public constructor(code: ClientErrorCode, message: string) {
    super(message);
    this.name = "ClientError";
    this.code = code;
  }
}

export interface WorkerLike {
  postMessage(message: WorkerRequest): void;
  terminate(): void;
  onmessage: ((event: MessageEvent) => void) | null;
}

export type WorkerFactory = () => WorkerLike;

interface Pending {
  readonly resolve: (result: WorkerResult) => void;
  readonly reject: (err: ClientError) => void;
  readonly timer: ReturnType<typeof setTimeout>;
}

export interface EncryptOutput {
  readonly nonceB64: string;
  readonly ciphertextB64: string;
}

/** Account keys sealed under a new master password, with that password's login proof. */
export interface SealedForPassword {
  readonly authKeyB64: string;
  readonly keyEncB64: string;
}

/** A fresh emergency kit: the recovery key in text form, the account keys sealed under it, and its login proof. */
export interface RecoveryKitMaterial {
  readonly recoveryKey: string;
  readonly keyEncB64: string;
  readonly authKeyB64: string;
}

export interface IdentityMaterial {
  readonly pubB64: string;
  readonly privEncB64: string;
}

export interface InviteMaterial {
  readonly secretB64: string;
  readonly tokenB64: string;
  readonly wrappedB64: string;
}

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;
type OutgoingRequest = DistributiveOmit<WorkerRequest, "id">;

function need(fields: FieldsResult, key: string): string {
  const value = fields[key];
  if (value === undefined) {
    throw new ClientError("bad-response", "Unexpected worker result.");
  }
  return value;
}

export class CryptoClient {
  private readonly worker: WorkerLike;
  private readonly timeoutMs: number;
  private readonly pending = new Map<number, Pending>();
  private nextId = 1;
  private terminated = false;

  public constructor(factory: WorkerFactory, timeoutMs: number = 120_000) {
    this.worker = factory();
    this.timeoutMs = timeoutMs;
    this.worker.onmessage = (event: MessageEvent): void => {
      this.handleMessage(event.data);
    };
  }

  public terminate(): void {
    this.terminated = true;
    for (const entry of this.pending.values()) {
      clearTimeout(entry.timer);
      entry.reject(new ClientError("terminated", "Client was terminated."));
    }
    this.pending.clear();
    this.worker.terminate();
  }

  public async unlock(password: string, saltB64: string, email: string): Promise<string> {
    const result = await this.send({ op: "unlock", password, saltB64, email });
    if (!isUnlockResult(result)) {
      throw new ClientError("bad-response", "Unexpected unlock result.");
    }
    return result.authKeyB64;
  }

  public async deriveAuthKey(password: string, saltB64: string, email: string): Promise<string> {
    const result = await this.send({ op: "deriveAuth", password, saltB64, email });
    if (!isUnlockResult(result)) {
      throw new ClientError("bad-response", "Unexpected derive result.");
    }
    return result.authKeyB64;
  }

  public async lock(): Promise<void> {
    const result = await this.send({ op: "lock" });
    if (!isLockResult(result)) {
      throw new ClientError("bad-response", "Unexpected lock result.");
    }
  }

  public async encrypt(plaintextB64: string, aadB64: string): Promise<EncryptOutput> {
    const result = await this.send({ op: "encrypt", plaintextB64, aadB64 });
    if (!isEncryptResult(result)) {
      throw new ClientError("bad-response", "Unexpected encrypt result.");
    }
    return { nonceB64: result.nonceB64, ciphertextB64: result.ciphertextB64 };
  }

  public async decrypt(nonceB64: string, ciphertextB64: string, aadB64: string): Promise<string> {
    const result = await this.send({ op: "decrypt", nonceB64, ciphertextB64, aadB64 });
    if (!isDecryptResult(result)) {
      throw new ClientError("bad-response", "Unexpected decrypt result.");
    }
    return result.plaintextB64;
  }

  /** Creates a fresh identity keypair; the private half is returned already wrapped with the wrap key. */
  public async identityCreate(): Promise<IdentityMaterial> {
    const fields = await this.fields({ op: "identityCreate" });
    return { pubB64: need(fields, "pubB64"), privEncB64: need(fields, "privEncB64") };
  }

  public async identityLoad(pubB64: string, privEncB64: string): Promise<void> {
    await this.fields({ op: "identityLoad", pubB64, privEncB64 });
  }

  /** Generates an org key, keeps it in the worker, and returns it sealed to our own identity. */
  public async orgCreate(orgId: string): Promise<string> {
    return need(await this.fields({ op: "orgCreate", orgId }), "wrappedB64");
  }

  public async orgOpen(orgId: string, wrappedB64: string): Promise<void> {
    await this.fields({ op: "orgOpen", orgId, wrappedB64 });
  }

  public async orgOpenInvite(orgId: string, secretB64: string, wrappedB64: string): Promise<void> {
    await this.fields({ op: "orgOpenInvite", orgId, secretB64, wrappedB64 });
  }

  public async orgSealFor(orgId: string, recipientPubB64: string): Promise<string> {
    return need(await this.fields({ op: "orgSealFor", orgId, recipientPubB64 }), "wrappedB64");
  }

  public async orgInviteCreate(orgId: string): Promise<InviteMaterial> {
    const fields = await this.fields({ op: "orgInviteCreate", orgId });
    return {
      secretB64: need(fields, "secretB64"),
      tokenB64: need(fields, "tokenB64"),
      wrappedB64: need(fields, "wrappedB64")
    };
  }

  public async inviteDerive(secretB64: string): Promise<string> {
    return need(await this.fields({ op: "inviteDerive", secretB64 }), "tokenB64");
  }

  public async orgEncrypt(orgId: string, plaintextB64: string, aadB64: string): Promise<EncryptOutput> {
    const fields = await this.fields({ op: "orgEncrypt", orgId, plaintextB64, aadB64 });
    return { nonceB64: need(fields, "nonceB64"), ciphertextB64: need(fields, "ciphertextB64") };
  }

  public async orgDecrypt(orgId: string, nonceB64: string, ciphertextB64: string, aadB64: string): Promise<string> {
    return need(await this.fields({ op: "orgDecrypt", orgId, nonceB64, ciphertextB64, aadB64 }), "plaintextB64");
  }

  public async orgRotateBegin(orgId: string): Promise<void> {
    await this.fields({ op: "orgRotateBegin", orgId });
  }

  public async orgRotateSealFor(orgId: string, recipientPubB64: string): Promise<string> {
    return need(await this.fields({ op: "orgRotateSealFor", orgId, recipientPubB64 }), "wrappedB64");
  }

  public async orgRotateReencrypt(
    orgId: string,
    nonceB64: string,
    ciphertextB64: string,
    aadB64: string
  ): Promise<EncryptOutput> {
    const fields = await this.fields({ op: "orgRotateReencrypt", orgId, nonceB64, ciphertextB64, aadB64 });
    return { nonceB64: need(fields, "nonceB64"), ciphertextB64: need(fields, "ciphertextB64") };
  }

  public async orgRotateCommit(orgId: string): Promise<void> {
    await this.fields({ op: "orgRotateCommit", orgId });
  }

  public async orgRotateAbort(orgId: string): Promise<void> {
    await this.fields({ op: "orgRotateAbort", orgId });
  }

  /** Keyed title digest. scope is "personal" or an org id. */
  public async titleHash(scope: string, title: string): Promise<string> {
    return need(await this.fields({ op: "titleHash", scope, title }), "hash");
  }

  /**
   * Loads the account keys after unlock. Pass null for an account from before the account keys:
   * the password-derived keys are adopted and returned sealed, to be stored on the server.
   */
  public async openAccountKeys(keyEncB64: string | null): Promise<string | undefined> {
    const fields = await this.fields({ op: "keysOpen", keyEncB64: keyEncB64 ?? undefined });
    return fields["keyEncB64"];
  }

  /** New account: random account keys, returned sealed under the password from unlock. */
  public async createAccountKeys(): Promise<string> {
    return need(await this.fields({ op: "keysCreate" }), "keyEncB64");
  }

  /** Seals the loaded account keys under another master password. The loaded keys stay as they are. */
  public async sealAccountKeysFor(password: string, saltB64: string, email: string): Promise<SealedForPassword> {
    const fields = await this.fields({ op: "keysSealFor", password, saltB64, email });
    return { authKeyB64: need(fields, "authKeyB64"), keyEncB64: need(fields, "keyEncB64") };
  }

  public async createRecoveryKit(): Promise<RecoveryKitMaterial> {
    const fields = await this.fields({ op: "recoveryCreate" });
    return { recoveryKey: need(fields, "recoveryKey"), keyEncB64: need(fields, "keyEncB64"), authKeyB64: need(fields, "authKeyB64") };
  }

  public async recoveryProof(recoveryKey: string): Promise<string> {
    return need(await this.fields({ op: "recoveryProof", recoveryKey }), "authKeyB64");
  }

  /** Loads the account keys with the recovery key instead of the master password. */
  public async recoveryOpen(recoveryKey: string, keyEncB64: string): Promise<void> {
    await this.fields({ op: "recoveryOpen", recoveryKey, keyEncB64 });
  }

  /** Passkey unlock: the loaded account keys sealed under the key from this PRF output. */
  public async sealForPasskey(prfB64: string): Promise<string> {
    return need(await this.fields({ op: "passkeySeal", prfB64 }), "keyEncB64");
  }

  /** Passkey sign-in: loads the account keys with the PRF output instead of the master password. */
  public async openWithPasskey(prfB64: string, keyEncB64: string): Promise<void> {
    await this.fields({ op: "passkeyOpen", prfB64, keyEncB64 });
  }

  /** Admin recovery: the loaded account keys sealed to the instance recovery public key. */
  public async sealEscrow(publicKeyB64: string): Promise<string> {
    return need(await this.fields({ op: "escrowSeal", publicKeyB64 }), "escrowB64");
  }

  /** New instance recovery key, kept in the worker; the secret comes back sealed to our identity. Needs the identity loaded. */
  public async instanceKeyCreate(): Promise<{ publicKeyB64: string; sealedKeyB64: string }> {
    const fields = await this.fields({ op: "instanceKeyCreate" });
    return { publicKeyB64: need(fields, "publicKeyB64"), sealedKeyB64: need(fields, "sealedKeyB64") };
  }

  public async instanceKeyOpen(publicKeyB64: string, sealedKeyB64: string): Promise<void> {
    await this.fields({ op: "instanceKeyOpen", publicKeyB64, sealedKeyB64 });
  }

  public async instanceKeySealFor(recipientPubB64: string): Promise<string> {
    return need(await this.fields({ op: "instanceKeySealFor", recipientPubB64 }), "sealedKeyB64");
  }

  /** Opens an account's escrow and seals its keys under a one-time password. */
  public async escrowReset(escrowB64: string, password: string, saltB64: string, email: string): Promise<SealedForPassword> {
    const fields = await this.fields({ op: "escrowReset", escrowB64, password, saltB64, email });
    return { authKeyB64: need(fields, "authKeyB64"), keyEncB64: need(fields, "keyEncB64") };
  }

  /**
   * Extension link: the worker seals its account keys and the extension's new session to the
   * extension's one-time key. Only the sealed blob comes back.
   */
  public async sealForExtension(input: Omit<SealForExtensionRequest, "id" | "op">): Promise<string> {
    return need(await this.fields({ ...input, op: "sealForExtension" }), "sealedB64");
  }

  private async fields(out: OutgoingRequest): Promise<FieldsResult> {
    const result = await this.send(out);
    if (!isFieldsResult(result)) {
      throw new ClientError("bad-response", "Unexpected worker result.");
    }
    return result;
  }

  private send(out: OutgoingRequest): Promise<WorkerResult> {
    if (this.terminated) {
      return Promise.reject(new ClientError("terminated", "Client was terminated."));
    }
    const id = this.nextId;
    this.nextId += 1;
    const req: WorkerRequest = { ...out, id };
    return new Promise<WorkerResult>((resolve, reject): void => {
      const timer = setTimeout((): void => {
        this.pending.delete(id);
        reject(new ClientError("worker-timeout", "Crypto worker timed out."));
      }, this.timeoutMs);
      this.pending.set(id, { resolve, reject, timer });
      this.worker.postMessage(req);
    });
  }

  private handleMessage(data: unknown): void {
    if (!isWorkerResponse(data)) {
      return;
    }
    const entry = this.pending.get(data.id);
    if (entry === undefined) {
      return;
    }
    this.pending.delete(data.id);
    clearTimeout(entry.timer);
    const res: WorkerResponse = data;
    if (res.ok) {
      entry.resolve(res.result);
    } else {
      entry.reject(new ClientError("worker-error", res.error));
    }
  }
}
